'use strict';

import TemplateHelper from './template-helper';
import Item from '../api/item';
import Expense from '../api/expense';
import deliveries from '../api/deliveries';
import delegateListener from '../modules/delegate-listener';
import Delivery from '../api/delivery';
import StorageEvent from '../api/storage-event';
import WebshopUser from '../api/webshopuser';
import PreStorageEvent from '../api/pre-storage-event';
import OrderDelivery from '../views/order-delivery.js';

const item = new Item(window.ITEM_ID);

const templateHelper = new TemplateHelper();

/**
 * This module handles ajax forms and other form related stuff
 */
class FormHelper {
  constructor(options) {
    const defaults = {
      formSelector: '.ajax-form',
      messageClass: 'message',
    };

    this.settings = Object.assign({}, defaults, options);
    this.s = this.settings;
  }

  /**
   * Initialize the module
   */
  init() {
    this._forms = Array.from(document.querySelectorAll(this.s.formSelector));
    this._bindEvents();
  }

  /**
   * Bind events to elements according to settings
   */
  _bindEvents() {
    const boundHandler = this._handleAjaxForm.bind(this);
    document.body.addEventListener('submit', delegateListener(this.s.formSelector, boundHandler));
  }

  /**
   * Handle the submit event of an ajax form.
   *
   * @param {Object} event The submit event
   */
  _handleAjaxForm(event) {
    const form = event.target;
    event.preventDefault();
    this._updateMessages(form, [], true);
    this._setLoadingState(form, true);
    this._performAction(form, form.getAttribute('data-action'));
  }

  /**
   * Toggle faux submit button enabled/disabled
   *
   * @param   {string}  elId - id of submit button that faux button controls
   * @param   {boolean} state - true: enable, false: disable
   * @returns {boolean} - false if invalid parameters, else true
   */
  fauxBtnToggler( elId, state ){
    if( !elId || typeof state === 'undefined' ) return false;
    // enable/disable faux buttons (labels) to prevent multiple clicks
    const fauxBtns = document.querySelectorAll(`label[for="${elId}"].button,label[data-for="${elId}"].button`);
    fauxBtns.forEach( fauxBtn => {

      if( state === false ){
        // disable faux btn
        fauxBtn.setAttribute('data-for',elId);
        fauxBtn.removeAttribute('for');
        fauxBtn.classList.add('button--disabled');
      }
      else {
        // enable faux btn
        fauxBtn.setAttribute('for',elId);
        fauxBtn.removeAttribute('data-for');
        fauxBtn.classList.remove('button--disabled');
      }
    });
    return true;
  }


  /**
   * Perform an action specified in the form's data-action attribute.
   *
   * @param {HTMLElement} form    The form element
   * @param {string}      action  The action to perform
   */
  _performAction(form, action) {
    const self = this;

    switch (action) {
    /**
     * Add comment on an item
     */
      case 'add-comment': {
        const commentTextarea = form.querySelector('[name="comment"]');
        const comment = commentTextarea.value;
        item.createComment(comment, false).then(response => {
          // Show possible messages
          self._updateMessages(form, response.messages, response.status === 'OK');

          if (response.status === 'OK') {
            // Add comment to the comments list

            const data = {
              author:        response.result.user,
              date:          response.result.created_time_unformatted,
              dateFormatted: response.result.created_time,
              content:       response.result.comment,
              important:     response.result.important,
              seen:          response.result.seen,
              commentId:     response.result.pid,
            };

            templateHelper.render('quote-box', data, document.getElementById('item__comments'), 'prepend');

            // Empty form
            commentTextarea.value = '';
          }

          // Enable form
          self._setLoadingState(form, false);
        });
        break;
      }

    /**
     * Create or update a delivery
     */
      case 'order-delivery': {

        if( self.orderDeliveryInProgress ) break; // prevent duplicate submits.
        self.orderDeliveryInProgress = true;
        self.fauxBtnToggler('order-delivery-submit',false); // disable faux btn

        const data = this._getFormData(form);
        data.item = window.ITEM_ID;
        data.deliveryPid = window.DELIVERY_PID;

        if(!Array.isArray(data.tasks)) {
          data.tasks = [];
        }

      /**
       * Common handler for order-delivery responses
       * @param {Object} response - The response returned by server
       */
        const handleResponse = (response) => {
          self._updateMessages(form, response.messages, response.status === 'OK');

          if(response.status === 'OK') {
            window.location.href = window.ITEM_VIEW_URL;
          }
          else {
            self._setLoadingState(form, false);
            self.orderDeliveryInProgress = false;
            self.fauxBtnToggler('order-delivery-submit',true);
          }
        };

        //Reset message box
        self._updateMessages(form, []);

        //Validate fields
        const requiredFields = Array.from(form.querySelectorAll('[required]'))
          .map(field => field.name)
          .filter((value, index, self) => self.indexOf(value) === index);

        if(requiredFields.some(field => !data[field])) {
          self._updateMessages(form, [window.ERRORS__FILL_REQUIRED_FIELDS]);
          self._setLoadingState(form, false);
          self.orderDeliveryInProgress = false;
          self.fauxBtnToggler('order-delivery-submit', true);
        } else {
          if(data.deliveryPid) {
            // Editing existing delivery
            const delivery = new Delivery(data.deliveryPid);
            delivery.patch(data).then(handleResponse);
          } else {
            // Creating a new delivery
            deliveries.post(data).then(handleResponse);
          }
        }

        break;
      }
      case 'new-expense': {
        const data = this._getFormData(form);
        const item = new Item(window.ITEM_ID);
        item.createExpense(data).then(response => {
          self._updateMessages(form, response.messages, response.status === 'OK');

          if(response.status === 'OK') {
            window.location.href = window.EXPENSE_VIEW_URL;
          }
          self._setLoadingState(form, false);
        });
        break;
      }
      case 'edit-expense': {
        const data = this._getFormData(form);
        const expense = new Expense(data.pid);
        expense.patch(data).then(response => {
          self._updateMessages(form, response.messages, response.status === 'OK');

          if(response.status === 'OK') {
            window.location.href = window.EXPENSE_VIEW_URL;
          }
          self._setLoadingState(form, false);
        });
        break;
      }
      case 'edit-location': {
        const data = this._getFormData(form);
        const item = new Item(window.ITEM_ID);
        item.updateLocation(data).then(response => {
          self._updateMessages(form, response.messages, response.status === 'OK');

          if(response.status === 'OK') {
            window.location.href = window.ITEM_VIEW_URL;
          }
        });
        self._setLoadingState(form, false);
        break;
      }
      case 'order-images': {
        const data = this._getFormData(form);
        const item = new Item(window.ITEM_ID);
        const description = data.order_images_description;
        if(typeof description !== 'undefined' && description.length > 0) {
          item.orderImages({
            description
          }).then(response => {
            // Alerts will do for now, consider custom modals later
            /* eslint-disable no-alert */
            if(response.status === 'OK') {
              alert(form.dataset.successMessage);
              window.location.href = window.ITEM_VIEW_URL;
            } else {
              alert(form.dataset.failMessage);
              self._setLoadingState(form, false);
            }
            /* eslint-enable no-alert */
          });
        }
        break;
      }
      case 'new-storage-event': {
        const data = this._getFormData(form);
        const storageEvent = new StorageEvent();
        storageEvent.post(data).then(response => {
          // Alerts will do for now, consider custom modals later
          /* eslint-disable no-alert */
          if(response.status === 'OK') {
            alert(form.dataset.successMessage);
            window.location.href = window.ITEM_VIEW_URL;
          } else {
            alert(form.dataset.failMessage);
            self._setLoadingState(form, false);
          }
          /* eslint-enable no-alert */
        });
        break;
      }

      case 'check-ssn': {
        const data = this._getFormData(form);
        const wsUser = new WebshopUser(data.user);

        wsUser.checkSSN(data.ssn).then(response => {
          self._updateMessages(form, response.messages, response.status === 'OK');
          self._setLoadingState(form, false);
        });

        break;
      }

      case 'pre-storage-event': {
        const data = this._getFormData(form);

      /**
       * Common handler for pre storage event responses
       * @param {Object} response - The response returned by server
       */
        const handleResponse = (response) => {
          self._updateMessages(form, response.messages, response.status === 'OK');

          if(response.status === 'OK') {
            self._setLoadingState(form, false);

            if(response.redirect) {
              window.location.href = response.redirect;
            }
          }
          else {
            self._setLoadingState(form, false);
          }
        };

        // Reset message box
        self._updateMessages(form, []);

        // Validate fields
        const requiredFields = Array.from(form.querySelectorAll('[required]'))
          .map(field => field.name)
          .filter((value, index, self) => self.indexOf(value) === index);

        if(requiredFields.some(field => !data[field])) {
          self._updateMessages(form, [window.ERRORS__FILL_REQUIRED_FIELDS]);
          self._setLoadingState(form, false);
        } else {
          if(data.storage_event_pid) {
            // Editing existing event
            const preStorageEvent = new PreStorageEvent(data.storage_event_pid);
            preStorageEvent.patch(data).then(handleResponse);
          } else {
            // Creating a new event
            const preStorageEvent = new PreStorageEvent();
            preStorageEvent.post(data).then(handleResponse);
          }
        }

        break;
      }

      case 'pre-storage-event-archive': {
        const data = this._getFormData(form);

        // Reset message box
        self._updateMessages(form, []);

        // Validate fields
        const requiredFields = Array.from(form.querySelectorAll('[required]'))
          .map(field => field.name)
          .filter((value, index, self) => self.indexOf(value) === index);

        if(requiredFields.some(field => !data[field])) {
          self._updateMessages(form, [window.ERRORS__FILL_REQUIRED_FIELDS]);
          self._setLoadingState(form, false);
        } else {
          const preStorageEvent = new PreStorageEvent(data.storage_event_pid);

          preStorageEvent.remove({reason: data.reason}).then(response => {
            // Alerts will do for now, consider custom modals later
            /* eslint-disable no-alert */
            if(response.status === 'OK') {
              alert(response.messages.join(', '));
              window.location.reload();
            } else {
              alert(response.messages.join(', '));
              self._setLoadingState(form, false);
            }
            /* eslint-enable no-alert */
          });
        }

        break;
      }

      case 'pre-storage-event-combine': {
        const data = this._getFormData(form);

        // Reset message box
        self._updateMessages(form, []);

        const item = new Item(data.item);

        item.combine(data.ghost_item).then(response => {
          // Alerts will do for now, consider custom modals later
          /* eslint-disable no-alert */
          if(response.status === 'OK') {
            alert(response.messages.join(', '));
            window.location.reload();
          } else {
            alert(response.messages.join(', '));
            self._setLoadingState(form, false);
          }
          /* eslint-enable no-alert */
        });

        break;
      }

      case 'set-delivery-images': {
        const data = this._getFormData(form);

        const imageCheckboxes = form.querySelectorAll('.image-grid__checkbox[id^="image_"]')
        const hideShowData = Array.from(imageCheckboxes).reduce((acc, el) => {
          if(el.checked) {
            acc.show.push(el.dataset.itemId);
          } else {
            acc.hide.push(el.dataset.itemId);
          }
          return acc;
        }, { hide: [], show: []});
        const item = new Item(data.pid);
        item.setDeliveryImages(hideShowData).then(response => {
          const { updatedImages } = response;
          if(updatedImages) {
            OrderDelivery.prototype.updateSelectedImages(updatedImages);
          }
          self._setLoadingState(form, false);
        });
        break;
      }

      default: {
        // No case defined for ${action}
      }
    }
  }

  /**
   * After sending a form, set it to loading state, disable submit buttons etc.
   * After the form is sent and response is received, remove loading state and enable submit buttons etc
   *
   * @param {HTMLElement} form  The form element
   * @param {boolean}     state True for entering loading mode, false for exiting loading mode
   */
  _setLoadingState(form, state) {
    const submitButtons = form.querySelectorAll('[type="submit"]:not(.implicit-submit),button:not([type="reset"]):not(.implicit-submit)');
    submitButtons.forEach(button => button.disabled = state);
  }

  /**
   * Update form messages, for example show an error message.
   *
   * @param {HTMLElement}   form      The form element
   * @param {Array}         messages  The error messages as an array or one error message as a string
   * @param {boolean}       success   If false, an error class is added to the message
   */
  _updateMessages(form, messages, success) {
    let messageContainer = form.querySelector('.' + this.s.messageClass);

    if (!messageContainer) {
      // Message container doesn't exist - create it
      messageContainer = document.createElement('div');
      messageContainer.classList.add(this.s.messageClass);
      form.insertBefore(messageContainer, form.childNodes[0]);
    }

    messageContainer.innerHTML = '';

    if (Array.isArray(messages) && messages.length > 0) {
      // Response includes a message. Show it to the user.
      const messagesUl = document.createElement('ul');

      messages.forEach(message => {
        const messageLi = document.createElement('li');
        messageLi.innerHTML = message;
        messagesUl.appendChild(messageLi);
      });

      messageContainer.appendChild(messagesUl);

      messageContainer.classList.add(this.s.messageClass + (success ? '--success' : '--error'));
      messageContainer.classList.remove(this.s.messageClass + (!success ? '--success' : '--error'));

      messageContainer.classList.remove('hidden');
    } else {
      messageContainer.classList.add('hidden');
    }
  }

  /**
   * Get data from a form
   *
   * @param   {HTMLElement} form  The form element
   * @returns {Object}            The form data
   */
  _getFormData(form) {
    const inputElements = Array.from(form.querySelectorAll('[name]'));

    let formData = {
      'stage-language': window.lang
    };

    inputElements.forEach(inputElement => {
      switch (inputElement.nodeName) {


        case 'INPUT':
          switch (inputElement.type) {
            case 'text':
            case 'hidden':
            case 'password':
            case 'button':
            case 'reset':
            case 'submit':
            case 'number':
              formData[inputElement.name] = inputElement.value;
              break;
            case 'checkbox':
              if (inputElement.checked) {
                if (typeof formData[inputElement.name] === 'undefined') {
                  formData[inputElement.name] = [];
                }
                formData[inputElement.name].push(inputElement.value);
              }
              break;
            case 'radio':
              if (inputElement.checked) {
                formData[inputElement.name] = inputElement.value;
              }
              break;
            case 'file':
              break;
          }
          break;

        case 'TEXTAREA':
          formData[inputElement.name] = inputElement.value;
          break;

        case 'SELECT':
          switch (inputElement.type) {
            case 'select-one':
              formData[inputElement.name] = inputElement.value;
              break;
            case 'select-multiple':
              formData[inputElement.name] = Array.from(inputElement.options).filter(option => option.selected).map(option => option.value);
              break;
          }
          break;

        case 'BUTTON':
          switch (inputElement.type) {
            case 'reset':
            case 'submit':
            case 'button':
              formData[inputElement.name] = inputElement.value;
              break;
          }
          break;
      }
    });
    return formData;
  }

}

export default FormHelper;
